'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { useAuthStore } from '@/stores/authStore'
import { useTranslation } from '@/lib/i18n/useTranslation'
import { Eye, EyeOff, LogIn, ShieldCheck, ArrowLeft } from 'lucide-react'
import Link from 'next/link'
import Logo43OS from '@/components/brand/Logo43OS'

export default function InstallerLoginPage() {
  const router = useRouter()
  const login = useAuthStore((s) => s.login)
  const confirmDevice = useAuthStore((s) => s.confirmDevice)
  const confirmTotp = useAuthStore((s) => s.confirmTotp)
  const { t } = useTranslation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [activationCode, setActivationCode] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Krok potwierdzenia: nowe urządzenie (kod z e-maila) LUB 2FA admina (TOTP).
  const [step, setStep] = useState<'login' | 'confirm'>('login')
  const [confirmMode, setConfirmMode] = useState<'device' | 'totp'>('device')
  const [challengeId, setChallengeId] = useState('')
  const [emailHint, setEmailHint] = useState('')
  const [code, setCode] = useState('')
  const [remember, setRemember] = useState(true)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const res = await login(email, password)
      if (res.requiresDeviceConfirmation) {
        setConfirmMode('device')
        setChallengeId(res.challengeId || '')
        setEmailHint(res.emailHint || '')
        setStep('confirm')
      } else if (res.requiresTotp) {
        setConfirmMode('totp')
        setChallengeId(res.challengeId || '')
        setStep('confirm')
      } else if (res.ok) {
        router.push('/installer/dashboard')
      } else {
        setError(res.error || t('invalidCredentials'))
      }
    } catch {
      setError(t('loginError'))
    } finally {
      setLoading(false)
    }
  }

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const res = confirmMode === 'totp'
        ? await confirmTotp(challengeId, code.trim())
        : await confirmDevice(challengeId, code.trim(), remember)
      if (res.ok) {
        router.push('/installer/dashboard')
      } else {
        setError(res.error || t('inst.wrongCode'))
      }
    } catch {
      setError(t('loginError'))
    } finally {
      setLoading(false)
    }
  }

  // ── Styl (czern + zolty Wordzik, jasny HERO-look) ──
  const inputCls =
    "w-full px-4 py-3 rounded-xl bg-white border border-[#E7E5DE] text-[#111111] " +
    "placeholder:text-[#B5B3AC] focus:border-[#111111] focus:ring-2 focus:ring-[#FFD400]/50 " +
    "outline-none transition-all text-sm"

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-[#FAFAF8]">
      {/* Subtelny jasny akcent (zoltawa poswiata u gory, bardzo delikatna) */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[640px] h-[640px] bg-[#FFD400]/10 rounded-full blur-3xl" />
      </div>

      {/* Login card */}
      <div className="relative z-10 w-full max-w-md mx-4">
        {/* Logo marki 43OS */}
        <div className="flex flex-col items-center text-center mb-8">
          <Logo43OS size={38} />
          <p className="text-[#888780] text-sm mt-4 tracking-[0.16em] uppercase">
            System operacyjny dla instalatorów
          </p>
        </div>

        {step === 'login' ? (
          /* ── Logowanie ── */
          <form
            onSubmit={handleSubmit}
            className="bg-white border border-[#E7E5DE] rounded-2xl p-8 shadow-[0_10px_40px_rgba(0,0,0,0.06)]"
          >
            <h2 className="text-xl font-semibold text-[#111111] mb-6">{t('login')}</h2>

            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Email */}
            <div className="mb-4">
              <label className="block text-sm text-[#888780] mb-1.5">{t('email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@firma.pl"
                required
                className={inputCls}
              />
            </div>

            {/* Password */}
            <div className="mb-4">
              <label className="block text-sm text-[#888780] mb-1.5">{t('password')}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('yourPassword')}
                  required
                  className={inputCls + " pr-12"}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888780] hover:text-[#111111] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Activation Code */}
            <div className="mb-6">
              <label className="block text-sm text-[#888780] mb-1.5">
                {t('activationCode')} <span className="text-[#B5B3AC]">({t('optional')})</span>
              </label>
              <input
                type="text"
                value={activationCode}
                onChange={(e) => setActivationCode(e.target.value)}
                placeholder="XXXX-XXXX"
                className={inputCls}
              />
            </div>

            {/* Submit — zolty CTA, czarny tekst */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl
                         bg-[#FFD400] text-[#111111] font-semibold hover:bg-[#FFE74A]
                         transition-all disabled:opacity-50 disabled:cursor-not-allowed
                         shadow-[0_6px_20px_rgba(255,212,0,0.35)] text-sm"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black/25 border-t-black rounded-full animate-spin" />
              ) : (
                <>
                  <LogIn className="w-4 h-4" />
                  {t('loginBtn')}
                </>
              )}
            </button>

            {/* Links */}
            <div className="mt-4 text-center">
              <Link href="/installer/reset" className="text-xs text-[#888780] hover:text-[#111111] transition-colors">
                {t('forgotPassword')}
              </Link>
            </div>
          </form>
        ) : (
          /* ── Potwierdzenie nowego urządzenia ── */
          <form
            onSubmit={handleConfirm}
            className="bg-white border border-[#E7E5DE] rounded-2xl p-8 shadow-[0_10px_40px_rgba(0,0,0,0.06)]"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="w-9 h-9 rounded-xl bg-[#FFD400]/20 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-[#111111]" />
              </span>
              <h2 className="text-xl font-semibold text-[#111111]">
                {confirmMode === 'totp' ? t('inst.twoFactorTitle') : t('inst.confirmNewDevice')}
              </h2>
            </div>
            <p className="text-sm text-[#888780] mb-6">
              {confirmMode === 'totp' ? (
                <>{t('inst.totpPrompt')}</>
              ) : (
                <>{t('inst.codeSentPrefix')} <span className="text-[#111111] font-medium">{emailHint}</span>{t('inst.codeSentSuffix')} {t('inst.deviceConfirmSecurity')}</>
              )}
            </p>

            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm text-[#888780] mb-1.5">{t('inst.confirmationCode')}</label>
              <input
                type="text"
                inputMode="numeric"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                required
                className="w-full px-4 py-3 rounded-xl bg-white border border-[#E7E5DE] text-[#111111]
                           text-center text-2xl tracking-[0.4em] font-mono placeholder:text-[#D4D2CB]
                           focus:border-[#111111] focus:ring-2 focus:ring-[#FFD400]/50 outline-none transition-all"
              />
            </div>

            {confirmMode === 'device' && (
              <label className="flex items-center gap-2 mb-6 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="w-4 h-4 accent-[#FFD400]"
                />
                <span className="text-sm text-[#888780]">{t('inst.trustDevice')}</span>
              </label>
            )}
            {confirmMode === 'totp' && <div className="mb-6" />}

            {/* Potwierdzenie — czarny przycisk (akcja wtorna) */}
            <button
              type="submit"
              disabled={loading || code.length < 6}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl
                         bg-[#111111] text-white font-semibold hover:bg-black
                         transition-all disabled:opacity-40 disabled:cursor-not-allowed text-sm"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <ShieldCheck className="w-4 h-4" />
                  {t('inst.confirmAndLogin')}
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => { setStep('login'); setCode(''); setError('') }}
              className="mt-4 w-full flex items-center justify-center gap-1 text-xs text-[#888780] hover:text-[#111111] transition-colors"
            >
              <ArrowLeft className="w-3 h-3" /> {t('inst.backToLogin')}
            </button>
          </form>
        )}

        {/* Register link */}
        <div className="mt-3 text-center">
          <Link
            href="/installer/register"
            className="text-xs text-[#888780] hover:text-[#111111] transition-colors"
          >
            {t('inst.noAccountYet')}{' '}
            <span className="text-[#111111] font-semibold [box-shadow:inset_0_-0.5em_0_rgba(255,212,0,0.5)]">
              {t('inst.registerAction')}
            </span>
          </Link>
        </div>

        <p className="text-center text-xs text-[#B5B3AC] mt-4">
          43OS · wersja robocza
        </p>
      </div>
    </div>
  )
}
